import { Palette, SemanticColors, Theme, useTheme } from "@amx/ux-twoui-theming";
import { twouiZIndexes, useUnmount } from "@amx/ux-twoui-utils";
import { AnimationStyles, HighContrastSelector, IRawStyle, IStackItemStyles, IStackStyles, Stack } from "@fluentui/react";
import { useId } from "@fluentui/react-hooks";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useEffect } from "react";
import { ViewportType } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { CloseButtonProps } from "../../shared/props";
import { getColorFromCorrectPalette } from "../../styles/colors";
import { boxShadowDepths } from "../../styles/constants";
import { IconName } from "../../styles/icons";
import { useViewport } from "../../styles/media";
import { Announced } from "../Announced/Announced";
import { Button, ButtonProps } from "../Buttons/Button/Button";
import { IconButton } from "../Buttons/IconButton/IconButton";
import { Icon } from "../Icon/Icon";
import { TruncatedText } from "../TruncatedText/TruncatedText";
import { useNotificationBarController, useNotificationBarId } from "./NotificationBarProvider/NotificationBarProvider";

export const defaultDismissTime = 10000;

export type NotificationBarType = "info" | "error";

export interface NotificationBarProps {
    /**
     * Name of the icon to render
     */
    readonly iconName?: "Completed" | "Error";
    /**
     * Display message
     */
    readonly message: string;
    /**
     * Message will be announced by narrator
     */
    readonly ariaMessage: string;
    /**
     * Optional action button
     */
    readonly notificationAction?: ButtonProps;
    /**
     * Optional show time of the notification bar. Default is 5 seconds.
     */
    readonly showTime?: number;

    /**
     * Optional type of notification bar (info or error).
     * If value is not provided, the default type is info.
     */
    readonly notificationBarType?: NotificationBarType;

    /**
     * Close button for the notification bar.
     */
    readonly closeButton: CloseButtonProps;

    /**
     * When true, will override the background color of info type to use theme colors. On error types, this property
     * is ignored.
     */
    readonly isThemeStyle?: boolean;
}

const notificationBarHostContainer = (): IRawStyle => {
    return {
        pointerEvents: "none",
        position: "fixed",
        width: "100%",
        bottom: 0,
        left: 0,
        zIndex: twouiZIndexes.twoUINotificationBar,
        padding: "0 0 10px 0"
    };

};

const getNotificationBarContainerStyle = (
    theme: Theme,
    notificationBarType: NotificationBarType,
    isThemeStyle: boolean,
    viewport: ViewportType
): IStackStyles => {
    let color: keyof Palette | keyof SemanticColors = "neutralHigh";
    if (notificationBarType === "error") {
        color = "severeWarning";
    } else if (isThemeStyle) {
        color = "themeLighterAlt";
    }
    const backgroundColor = getColorFromCorrectPalette(theme, color);

    let minWidthBasedOnViewport: string = "400px";
    let maxWidthBasedOnViewport: string = "800px";

    if (viewport === "s") {
        // padding for small viewport is 16px on both left and right side
        minWidthBasedOnViewport = "min(400px, calc(100vw - 32px))";
        maxWidthBasedOnViewport = "calc(100vw - 32px)";
    } else if (viewport === "m") {
        // padding for medium viewport is 32px on both left and right side
        maxWidthBasedOnViewport = "calc(100vw - 64px)";
    } else if (viewport === "l") {
        // padding for medium viewport is 48px on both left and right side
        maxWidthBasedOnViewport = "min(800px, calc(100vw - 96px))";
    }

    return {
        root: {
            minHeight: 48,
            maxHeight: 92,
            minWidth: minWidthBasedOnViewport,
            maxWidth: maxWidthBasedOnViewport,
            padding: "0 16px 0 16px",
            boxShadow: boxShadowDepths.controls,
            backgroundColor,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pointerEvents: "auto",
            [HighContrastSelector]: {
                outline: "1px solid highlight",
            }
        }
    };
};

/**
 * Interface to control the notification bar
 */
export interface NotificationBarRef {
    /**
     * Show the notification bar
     */
    readonly showNotificationBar: () => void;
    /**
     * Hide the notification bar
     */
    readonly hideNotificationBar: () => void;
}

type NotificationBarShowStatus = "visible" | "hidden" | "slideOut";

export const NotificationBar = forwardRef<NotificationBarRef, NotificationBarProps>(({
    iconName,
    message,
    ariaMessage,
    notificationAction,
    closeButton,
    showTime = defaultDismissTime,
    notificationBarType = "info",
    isThemeStyle = false
}, ref) => {
    const notificationBarId = useNotificationBarId();
    useComponentLogger("NotificationBar");

    const notificationBarHandler = useNotificationBarController();

    const stackId = useId();
    const theme = useTheme();
    const viewport = useViewport();
    const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isControlled = ref === null;
    const [showStatus, setShowStatus] = useState<NotificationBarShowStatus>(() => isControlled ? "visible" : "hidden");
    const startTime = useRef(0);
    const minOnLeaveDisplayTime = 1000;

    useUnmount(() => clearTimeout(timeoutId.current!));

    useEffect(() => {
        if (isControlled) {
            showNotificationBar();
        }
    }, []);

    const onGettingUserFocus = () => {
        clearTimeout(timeoutId.current!);
    };

    const onLosingUserFocus = () => {
        if (showStatus === "visible") {
            const remainingShowTime = showTime - (Date.now() - startTime.current);
            timeoutId.current = setTimeout(() => {
                hideNotificationBar();
            }, Math.max(remainingShowTime, minOnLeaveDisplayTime));
        }
    };

    const showNotificationBar = () => {
        clearTimeout(timeoutId.current!);

        startTime.current = Date.now();

        timeoutId.current = setTimeout(() => {
            hideNotificationBar();
        }, showTime);

        setShowStatus("visible");
    };

    const animationEnd = () => {
        const notificationBarContainer = document.getElementById(stackId);
        notificationBarContainer?.removeEventListener("webkitAnimationEnd", animationEnd);
        notificationBarContainer?.removeEventListener("animationend", animationEnd);
        setShowStatus(prevStatus => prevStatus === "slideOut" ? "hidden" : prevStatus);
        notificationBarHandler?.removeNotificationBar(notificationBarId);
    };

    const hideNotificationBar = () => {
        clearTimeout(timeoutId.current!);

        const notificationBarContainer = document.getElementById(stackId);
        notificationBarContainer?.addEventListener("webkitAnimationEnd", animationEnd);
        notificationBarContainer?.addEventListener("animationend", animationEnd);
        setShowStatus("slideOut");
    };

    useImperativeHandle(ref, () => ({
        showNotificationBar,
        hideNotificationBar
    }));

    const iconColor = notificationBarType === "error" ? "errorIcon" : "successIcon";
    const textType = notificationBarType === "error" ? "errorBlockIcon" : "bodyPrimaryText";
    const buttonType = notificationBarType === "error" ? "default" : "primary";
    const closeButtonColor = notificationBarType === "error" ? "errorIcon" : "black";

    const iconButtonStyles: IStackItemStyles = {
        root: {
            marginLeft: "auto",
            marginRight: "-8px" //We need to offset the margin for icon space to match the design.
        }
    };

    const truncatedTextStyles: IStackItemStyles = {
        root: {
            margin: "12px 0px 12px 0px"
        }
    };

    if (showStatus === "hidden") {
        return <></>;
    }

    return (
        <Stack
            id={stackId}
            styles={{
                root: [
                    showStatus === "visible" ? AnimationStyles.slideUpIn20 : AnimationStyles.slideDownOut20,
                    isControlled ? undefined : notificationBarHostContainer()
                ]
            }}
            horizontalAlign="center"
            verticalAlign="center"
        >
            <Stack
                onMouseEnter={onGettingUserFocus}
                onMouseLeave={onLosingUserFocus}
                onFocus={onGettingUserFocus}
                onBlur={onLosingUserFocus}
                tokens={{
                    childrenGap: 8
                }}
                styles={getNotificationBarContainerStyle(theme, notificationBarType, isThemeStyle, viewport)}
                horizontal={true}
            >
                {iconName && (
                    <Stack.Item align="auto">
                        <Icon iconName={iconName as IconName} size="16px" color={iconColor} />
                    </Stack.Item>
                )}

                <Stack.Item align="auto" grow={true} styles={truncatedTextStyles}>
                    <TruncatedText variant={textType} lines={3}>{message}</TruncatedText>
                </Stack.Item>

                {notificationAction && (
                    <Stack.Item align="auto" disableShrink={true}>
                        <Button {...notificationAction} type={buttonType} />
                    </Stack.Item>
                )}

                <Stack.Item align="auto" styles={iconButtonStyles}>
                    <IconButton
                        iconName="Cancel"
                        iconColor={closeButtonColor}
                        {...closeButton}
                        onClick={hideNotificationBar}
                    />
                </Stack.Item>
            </Stack >
            <Announced message={ariaMessage} ariaLive="assertive" />
        </Stack >
    );
});

export const NotificationBarContainerHost: React.FC = ({ children }) => {
    return (
        <Stack
            styles={{
                root: [
                    notificationBarHostContainer()
                ]
            }}
            tokens={{
                childrenGap: 8
            }}
        >
            {children}
        </Stack>
    );
};
